import { useState, useEffect } from 'react'
import api from '../../services/api'

interface Utente {
  id: number
  email: string
  nome: string
  cognome?: string
  ruolo: string
  attivo: boolean
  createdAt: string
}

/**
 * Pagina gestione utenti admin
 */
export default function AdminUtenti() {
  const [utenti, setUtenti] = useState<Utente[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchUtenti()
  }, [])

  const fetchUtenti = async () => {
    try {
      // L'API admin restituisce un array diretto
      const data = await api.get<Utente[]>('/admin/utenti')
      setUtenti(data ?? [])
    } catch (error) {
      console.error('Errore caricamento utenti:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleRuolo = async (utente: Utente) => {
    const nuovoRuolo = utente.ruolo === 'admin' ? 'utente' : 'admin'
    if (!confirm(`Vuoi ${nuovoRuolo === 'admin' ? 'promuovere' : 'rimuovere i privilegi admin di'} ${utente.nome}?`)) {
      return
    }

    try {
      await api.patch(`/admin/utenti/${utente.id}`, { ruolo: nuovoRuolo })
      setUtenti(utenti.map(u =>
        u.id === utente.id ? { ...u, ruolo: nuovoRuolo } : u
      ))
    } catch (error) {
      console.error('Errore aggiornamento utente:', error)
    }
  }

  const toggleAttivo = async (utente: Utente) => {
    try {
      await api.patch(`/admin/utenti/${utente.id}`, { attivo: !utente.attivo })
      setUtenti(utenti.map(u =>
        u.id === utente.id ? { ...u, attivo: !u.attivo } : u
      ))
    } catch (error) {
      console.error('Errore aggiornamento utente:', error)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-night-light border border-gray-800 rounded-xl p-4 animate-pulse">
            <div className="h-6 w-48 bg-gray-700 rounded mb-2"></div>
            <div className="h-4 w-32 bg-gray-700 rounded"></div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <p className="text-gray-400">{utenti?.length ?? 0} utenti registrati</p>

      <div className="bg-night-light border border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-800/50">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Utente</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Email</th>
              <th className="text-center px-6 py-4 text-sm font-medium text-gray-400">Ruolo</th>
              <th className="text-center px-6 py-4 text-sm font-medium text-gray-400">Attivo</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Registrato</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {(utenti || []).map(utente => (
              <tr key={utente.id} className="hover:bg-gray-800/30">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center">
                      <span className="text-gold font-medium">
                        {utente.nome.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-white">{utente.nome} {utente.cognome}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-gray-400">{utente.email}</span>
                </td>
                <td className="px-6 py-4 text-center">
                  <button
                    onClick={() => toggleRuolo(utente)}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      utente.ruolo === 'admin'
                        ? 'bg-gold/20 text-gold'
                        : 'bg-gray-700 text-gray-400'
                    }`}
                  >
                    {utente.ruolo}
                  </button>
                </td>
                <td className="px-6 py-4 text-center">
                  <button
                    onClick={() => toggleAttivo(utente)}
                    className={`w-10 h-6 rounded-full transition-colors relative ${
                      utente.attivo ? 'bg-green-500' : 'bg-gray-600'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      utente.attivo ? 'left-5' : 'left-1'
                    }`} />
                  </button>
                </td>
                <td className="px-6 py-4">
                  <span className="text-gray-400 text-sm">
                    {new Date(utente.createdAt).toLocaleDateString('it-IT')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
